import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, Send, Loader2, MessageSquare, ShieldAlert, CheckCircle2, EyeOff, Trash2, RotateCcw, Shield, X, ChevronLeft, ChevronRight } from "lucide-react";
import FontSizeToggle from "../components/FontSizeToggle";
import DOMPurify from "dompurify";
import { api, adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });

const avatarHead = (name) =>
  `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(name)}&direction=2&head_direction=3&size=b&action=std&gesture=sml&headonly=1`;

const categoryColor = (c) => {
  switch (c) {
    case "Jubiläum": return "bg-[#ff9800]/15 text-[#ffb84d] border-[#ff9800]/30";
    case "News":     return "bg-[#7c4dff]/15 text-[#b39bff] border-[#7c4dff]/30";
    case "Retro":    return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
    case "Glitch":   return "bg-rose-500/10 text-rose-300 border-rose-500/30";
    case "Community":return "bg-sky-500/10 text-sky-300 border-sky-500/30";
    case "Rare":     return "bg-amber-500/10 text-amber-300 border-amber-500/30";
    case "Event":    return "bg-pink-500/10 text-pink-300 border-pink-500/30";
    default:         return "bg-white/5 text-zinc-300 border-white/10";
  }
};

const ArticleDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const { user, userToken, adminToken, ready } = useAuth();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const contentRef = useRef(null);
  const [fontSize, setFontSize] = useState(() => {
    const stored = parseInt(localStorage.getItem("habthek-news-font-size") || "16", 10);
    return Number.isFinite(stored) && stored >= 13 && stored <= 22 ? stored : 16;
  });
  useEffect(() => {
    localStorage.setItem("habthek-news-font-size", String(fontSize));
  }, [fontSize]);

  const canModerate = !!adminToken || !!(user && user.permissions && user.permissions.comments_moderate);
  const modApi = useMemo(() => (canModerate ? adminApi(adminToken || userToken) : null), [canModerate, adminToken, userToken]);

  const onHide = async (c) => {
    const reason = window.prompt("Grund fuer das Verbergen? (optional)") ?? "";
    if (reason === null) return;
    try {
      await modApi.hideComment(c.id, reason);
      setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, status: "hidden", text: "" } : x));
      toast({ title: "Kommentar verborgen" });
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };
  const onModDelete = async (c) => {
    if (!window.confirm("Kommentar dauerhaft loeschen? (Platzhalter bleibt sichtbar)")) return;
    const reason = window.prompt("Grund fuer das Loeschen? (optional)") ?? "";
    try {
      await modApi.deleteComment(c.id, reason);
      setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, status: "deleted", text: "" } : x));
      toast({ title: "Kommentar geloescht" });
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };
  const onRestore = async (c) => {
    try {
      await modApi.restoreComment(c.id);
      // text neu laden
      const fresh = await api.listComments(article.id);
      setComments(fresh);
      toast({ title: "Kommentar wiederhergestellt" });
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const a = await api.getArticle(id);
        setArticle(a);
        const c = await api.listComments(id);
        setComments(c);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Galerie-Click-Handler: Lightbox öffnen bei Click auf .news-gallery img.
  // Event-Delegation auf document - funktioniert unabhängig vom Ref-Timing.
  useEffect(() => {
    if (!article) return;
    const handler = (e) => {
      const target = e.target;
      if (!(target && target.tagName === "IMG")) return;
      const gallery = target.closest(".news-gallery");
      if (!gallery) return;
      e.preventDefault();
      const imgs = Array.from(gallery.querySelectorAll("img"));
      const urls = imgs.map((im) => im.getAttribute("src")).filter(Boolean);
      const idx = imgs.indexOf(target);
      if (idx >= 0 && urls.length > 0) {
        setLightbox({ open: true, images: urls, index: idx });
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [article]);

  // Keyboard-Steuerung für Lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox((s) => ({ ...s, open: false }));
      else if (e.key === "ArrowLeft") setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }));
      else if (e.key === "ArrowRight") setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length }));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox.open]);

  const token = userToken || adminToken;
  const isVerified = !!adminToken || !!user?.verified;
  const canComment = !!token && isVerified;

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim() || !canComment) return;
    setSending(true);
    try {
      const c = await api.addComment(token, id, { text });
      setComments((prev) => [c, ...prev]);
      setText("");
      toast({ title: "Kommentar gesendet" });
    } catch (err) {
      toast({
        title: "Fehler",
        description: err?.response?.data?.detail || "Kommentar konnte nicht gesendet werden",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading || !ready) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-zinc-400">
        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-white">Artikel nicht gefunden</h1>
        <Link to="/news" className="mt-4 inline-block text-[#b39bff]">Zurück zur Übersicht</Link>
      </div>
    );
  }

  const safeHtml = article.content_html
    ? DOMPurify.sanitize(article.content_html, {
        ADD_TAGS: ["table", "thead", "tbody", "tr", "td", "th", "hr", "div"],
        ADD_ATTR: ["target", "rel", "class", "style", "width", "height", "align", "colspan", "rowspan", "loading", "data-gallery"],
        ALLOW_DATA_ATTR: false,
      })
    : "";

  // Galerie-Bilder aus dem Body extrahieren -> separate Sektion am Artikel-Ende.
  // Erkennt sowohl <div class="news-gallery"> (neuer Quill-Blot) als auch lose
  // <img alt="Galerie-Bild"> die Quill 2.x bei aelteren Artikeln nach Wrapper-Stripping
  // ohne <div>-Container im DOM gelassen hat.
  // Iter48: Explizite Galerie aus `gallery_images`-Feld + Legacy-Container.
  // Normale Inline-<img> bleiben dort, wo der Autor sie platziert hat (inkl. Alignment).
  const { bodyHtml, galleryImages } = (() => {
    const explicit = Array.isArray(article.gallery_images) ? article.gallery_images.filter(Boolean) : [];
    if (!safeHtml || typeof window === "undefined") return { bodyHtml: safeHtml, galleryImages: explicit };
    const wrap = document.createElement("div");
    wrap.innerHTML = safeHtml.replace(/\u00A0/g, " ");
    const urls = [...explicit];
    const pushUrl = (u) => { if (u && !urls.includes(u)) urls.push(u); };

    // 1) Explizite Galerie-Container
    wrap.querySelectorAll(".news-gallery").forEach((g) => {
      g.querySelectorAll("img").forEach((im) => pushUrl(im.getAttribute("src")));
      g.remove();
    });

    // 2) Legacy: <img alt="Galerie-Bild"> ohne Wrapper
    wrap.querySelectorAll('img[alt="Galerie-Bild"]').forEach((im) => {
      pushUrl(im.getAttribute("src"));
      im.remove();
    });

    // 3) Aufraeumen: leere <p>-Tags (nur wenn wirklich nichts mehr drin ist)
    wrap.querySelectorAll("p").forEach((p) => {
      if (!p.textContent.trim() && p.querySelectorAll("img,iframe,video").length === 0) {
        p.remove();
      }
    });

    return { bodyHtml: wrap.innerHTML, galleryImages: urls };
  })();

  return (
    <article className="mx-auto max-w-5xl px-4 py-8">
      <Link to="/news" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Alle Artikel
      </Link>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
        {/* HAUPTSPALTE */}
        <div className="min-w-0">
          {/* Header-Banner mit Titel + Excerpt als Overlay */}
          <header className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b]">
            {article.header_image ? (
              <img src={article.header_image} alt="" className="hb-pixel-img h-64 w-full object-cover md:h-80" />
            ) : (
              <div className="h-64 w-full bg-gradient-to-br from-[#1a1f29] to-[#10141b] md:h-80" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f141b] via-[#0f141b]/55 to-transparent" />
            <div className="absolute left-4 top-4">
              <span className={`inline-flex items-center rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${categoryColor(article.category)}`}>
                {article.category}
              </span>
            </div>
            <div className="absolute inset-x-4 bottom-4 md:inset-x-6 md:bottom-6">
              <h1 className="text-2xl md:text-4xl font-bold leading-tight text-white drop-shadow-md break-words">
                {article.title}
              </h1>
              {article.excerpt && (
                <p data-testid="article-excerpt" className="mt-2 text-sm md:text-base italic leading-snug text-zinc-100/95 drop-shadow line-clamp-3 break-words max-w-[70ch]">
                  {article.excerpt}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-zinc-300">
                <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {article.author}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(article.date)}{article.time ? ` · ${article.time} Uhr` : ""}</span>
              </div>
            </div>
          </header>

          {/* Body — Magazin-Layout mit einstellbarer Schriftgröße */}
          <div className="mt-5 rounded-2xl border border-white/10 bg-[#0f141b] px-5 py-7 md:px-8 md:py-9 overflow-hidden">
            <FontSizeToggle
              storageKey="habthek-news-font-size"
              initial={16}
              onChange={setFontSize}
              data-testid="article-font-size"
              className="mb-4"
            />
            {bodyHtml ? (
              <div
                ref={contentRef}
                lang="de"
                className="news-content mx-auto max-w-[72ch]"
                style={{ "--news-font-size": `${fontSize}px` }}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            ) : (
              <div
                ref={contentRef}
                lang="de"
                className="news-content mx-auto max-w-[72ch]"
                style={{ "--news-font-size": `${fontSize}px` }}
              >
                {(article.body || []).map((p, i) => (<p key={i}>{p}</p>))}
              </div>
            )}

            {/* Galerie-Sektion am Artikel-Ende (extrahiert aus content_html) */}
            {galleryImages.length > 0 && (
              <section className="mt-10 border-t border-white/10 pt-8" data-testid="article-gallery">
                <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-[#b39bff]">
                  Galerie · {galleryImages.length} {galleryImages.length === 1 ? "Bild" : "Bilder"}
                </h2>
                <div className="news-gallery-end">
                  {galleryImages.map((src, idx) => (
                    <button
                      key={src + idx}
                      type="button"
                      onClick={() => setLightbox({ open: true, images: galleryImages, index: idx })}
                      className="news-gallery-end-tile"
                      data-testid={`article-gallery-tile-${idx}`}
                      aria-label={`Bild ${idx + 1} von ${galleryImages.length} oeffnen`}
                    >
                      <img src={src} alt={`Galerie ${idx + 1}`} loading="lazy" />
                      <span className="news-gallery-end-overlay" aria-hidden>
                        <span className="news-gallery-end-zoom">⤢</span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}
            {(article.badges || []).length > 0 && (
              <div className="mt-6 rounded-xl border border-[#7c4dff]/25 bg-[#7c4dff]/5 p-4" data-testid="article-badges">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-[#b39bff]">Badges zu diesem Artikel</div>
                <div className="flex flex-wrap gap-3">
                  {article.badges.map((b) => (
                    <div key={b.code} className="flex items-center gap-2 rounded-lg border border-white/5 bg-[#10141b] p-2 pr-3" title={b.description || ""}>
                      <img src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`} alt={b.code} className="hb-pixel-img h-10 w-10" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-white">{b.name || b.code}</div>
                        {b.description && <div className="line-clamp-2 max-w-[260px] text-[11px] text-zinc-400">{b.description}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* KOMMENTAR-SPALTE (rechts) */}
        <aside className="lg:sticky lg:top-24 lg:self-start lg:h-[calc(100vh-7rem)]">
          <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#0f141b]">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <MessageSquare className="h-4 w-4 text-[#b39bff]" />
                Kommentare ({comments.filter((c) => c.status !== "deleted").length})
              </div>
              {canModerate && (
                <span data-testid="moderator-badge" className="inline-flex items-center gap-1 rounded bg-[#7c4dff]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#b39bff]">
                  <Shield className="h-3 w-3" /> Moderator
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {comments.map((c) => {
                if (c.status === "deleted") {
                  return (
                    <div key={c.id} data-testid={`comment-${c.id}-deleted`} className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 text-xs">
                      <Trash2 className="h-3.5 w-3.5 shrink-0 text-rose-300" />
                      <div className="flex-1 italic text-rose-200/90">
                        Diese Nachricht wurde von einem Moderator gelöscht.
                      </div>
                      {canModerate && (
                        <Button onClick={() => onRestore(c)} className="btn-ghost shrink-0 rounded px-2 py-0.5 text-[10px]" title="Wiederherstellen">
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                }
                if (c.status === "hidden") {
                  return (
                    <div key={c.id} data-testid={`comment-${c.id}-hidden`} className="flex items-center gap-2 rounded-lg border border-[#ffb84d]/40 bg-[#ffb84d]/10 p-2 text-xs">
                      <EyeOff className="h-3.5 w-3.5 shrink-0 text-[#ffb84d]" />
                      <div className="flex-1 italic text-[#ffb84d]">
                        Dieser Kommentar wurde verborgen.
                      </div>
                      {canModerate && (
                        <Button onClick={() => onRestore(c)} className="btn-ghost shrink-0 rounded px-2 py-0.5 text-[10px]" title="Wiederherstellen">
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={c.id} data-testid={`comment-${c.id}`} className="group flex gap-2 rounded-lg border border-white/5 bg-[#10141b] p-2">
                    {/* Avatar mit Glow NUR hinter dem Kopf; Badge ist klein und sitzt am Rand */}
                    <div className="relative shrink-0 h-9 w-9">
                      {(() => {
                        const glow = c.user_meta?.custom_glow_color || c.project_badge?.color;
                        if (!glow) return null;
                        return (
                          <div
                            aria-hidden
                            className="comment-glow intense absolute left-1/2 top-1/2 h-14 w-14 rounded-full pointer-events-none"
                            style={{ background: `radial-gradient(circle, ${glow}ff 0%, ${glow}55 40%, ${glow}00 75%)`, transform: "translate(-50%, -50%)" }}
                          />
                        );
                      })()}
                      <img src={avatarHead(c.habbo_name || c.author)} alt="" className="hb-pixel-img relative h-9 z-10" />
                      {c.project_badge && (
                        <img
                          src={`https://images.habbo.com/c_images/album1584/${c.project_badge.code}.gif`}
                          alt={c.project_badge.code}
                          title={`${c.project_badge.project_title}${c.project_badge.role ? " – " + c.project_badge.role : ""}`}
                          data-testid={`comment-${c.id}-project-badge`}
                          className="hb-pixel-img absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-[#0f141b] ring-1 z-20"
                          style={{ ['--tw-ring-color']: c.user_meta?.custom_glow_color || c.project_badge.color, boxShadow: `0 0 4px ${c.user_meta?.custom_glow_color || c.project_badge.color}` }}
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1 text-xs">
                        <div className="flex min-w-0 items-center gap-1 flex-wrap">
                          <Link
                            to={`/u/${encodeURIComponent(c.habbo_name || c.author || "")}`}
                            className="truncate font-semibold text-[#b39bff] hover:underline"
                            style={c.user_meta?.name_color ? { color: c.user_meta.name_color } : undefined}
                          >{c.habbo_name || c.author}</Link>
                          {c.user_meta?.tier && c.user_meta.tier.id !== "bronze" && (
                            <span
                              data-testid={`comment-${c.id}-tier`}
                              className="shrink-0 rounded-full px-1.5 py-0 text-[9px] font-bold tracking-wider"
                              style={{ background: `${c.user_meta.tier.color}22`, color: c.user_meta.tier.color }}
                              title={`${c.user_meta.tier.label} – ${c.user_meta.points} Punkte`}
                            >
                              {c.user_meta.tier.icon} {c.user_meta.tier.label}
                            </span>
                          )}
                          {c.team_badge && (
                            <span
                              data-testid={`comment-${c.id}-team-badge`}
                              className="shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300"
                              title={`Team: ${c.team_badge.position}`}
                            >
                              TEAM
                            </span>
                          )}
                          {c.project_badge && (
                            <span
                              className="shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                              style={{ background: `${c.project_badge.color}22`, color: c.project_badge.color }}
                              title={c.project_badge.project_title}
                            >
                              {c.project_badge.project_title.split(" ")[0]}
                            </span>
                          )}
                        </div>
                        <div className="shrink-0 text-zinc-500">{new Date(c.created_at).toLocaleDateString("de-DE")}</div>
                      </div>
                      <p className="mt-0.5 whitespace-pre-line text-xs text-zinc-200 break-words">{c.text}</p>
                      {canModerate && (
                        <div className="mt-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => onHide(c)}
                            data-testid={`comment-${c.id}-hide-btn`}
                            title="Verbergen"
                            className="inline-flex items-center gap-1 rounded border border-[#ffb84d]/40 bg-[#ffb84d]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#ffb84d] hover:bg-[#ffb84d]/20"
                          >
                            <EyeOff className="h-3 w-3" /> Verbergen
                          </button>
                          <button
                            type="button"
                            onClick={() => onModDelete(c)}
                            data-testid={`comment-${c.id}-delete-btn`}
                            title="Loeschen"
                            className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-rose-300 hover:bg-rose-500/20"
                          >
                            <Trash2 className="h-3 w-3" /> Loeschen
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {comments.length === 0 && (
                <div className="py-8 text-center text-xs text-zinc-500">Noch keine Kommentare.</div>
              )}
            </div>

            {/* Eingabe */}
            <div className="border-t border-white/10 p-3">
              {canComment ? (
                <form onSubmit={submitComment} className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    {user?.habbo_name && <img src={avatarHead(user.habbo_name)} alt="" className="hb-pixel-img h-7" />}
                    <span>als <span className="font-semibold text-white">{user?.habbo_name || "Admin"}</span></span>
                    {user?.verified && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                  </div>
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Dein Kommentar ..."
                    rows={2}
                    maxLength={1000}
                    className="resize-none bg-[#10141b] border-white/10 text-sm text-zinc-200 placeholder:text-zinc-500"
                  />
                  <Button type="submit" disabled={sending || !text.trim()} className="btn-primary w-full rounded-lg px-3 py-2 text-sm font-semibold">
                    {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                    Senden
                  </Button>
                </form>
              ) : (
                <div className="flex items-start gap-2 rounded-lg border border-[#ffb84d]/30 bg-[#ffb84d]/5 p-3">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#ffb84d]" />
                  <div className="text-xs text-zinc-200">
                    <div className="font-semibold text-white">Nur verifizierte Habbos können kommentieren.</div>
                    <div className="mt-2 flex gap-2">
                      {!token && (
                        <>
                          <Link to="/login" className="btn-ghost rounded-md px-2 py-1 text-xs">Login</Link>
                          <Link to="/registrieren" className="btn-primary rounded-md px-2 py-1 text-xs font-semibold">Registrieren</Link>
                        </>
                      )}
                      {token && !isVerified && (
                        <Link to="/verifizieren" className="btn-primary rounded-md px-2 py-1 text-xs font-semibold">Habbo verifizieren</Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Lightbox */}
      {lightbox.open && lightbox.images.length > 0 && (
        <div
          className="news-lightbox"
          onClick={() => setLightbox((s) => ({ ...s, open: false }))}
          data-testid="news-lightbox"
        >
          <button
            type="button"
            className="news-lightbox-btn news-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox((s) => ({ ...s, open: false })); }}
            aria-label="Schliessen"
            data-testid="lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          {lightbox.images.length > 1 && (
            <>
              <button
                type="button"
                className="news-lightbox-btn news-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length })); }}
                aria-label="Vorheriges"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                className="news-lightbox-btn news-lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length })); }}
                aria-label="Naechstes"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="news-lightbox-counter" data-testid="lightbox-counter">
                {lightbox.index + 1} / {lightbox.images.length}
              </div>
            </>
          )}
          <img
            key={lightbox.index}
            src={lightbox.images[lightbox.index]}
            alt="Galerie"
            className="news-lightbox-img"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      )}
    </article>
  );
};

export default ArticleDetail;
